
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, clearAuth } from '../../../store/slices/authSlice';
import { resetPracticeState } from '../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { useMediaQuery } from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faGear,
  faShieldHalved,
  faEnvelope,
  faRightFromBracket,
  faCircleQuestion,
  faBook,
} from '@fortawesome/free-solid-svg-icons';
import ChangePasswordScreen from './ChangePasswordScreen';
import EmailNotificationsScreen from './EmailNotificationsScreen';
import logo from '../../../assets/logo.png';
import sidebarIcon from '../../../assets/icon_sidebar.png';
import './Header.css';

const Header = ({ user, onToggleSidebar, sidebarOpen }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1199 });
  const isDesktop = useMediaQuery({ minWidth: 1200 });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { breadcrumbs } = useBreadcrumb();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [activeScreen, setActiveScreen] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (activeScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const handleScreenEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveScreen(null);
      }
    };

    document.addEventListener('keydown', handleScreenEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleScreenEscape);
    };
  }, [activeScreen]);

  // Logout must feel instant: clear local auth + navigate immediately,
  // then call backend logout in background.
  const handleLogout = () => {
    try {
      dispatch(resetPracticeState());
      dispatch(clearAuth());
    } catch (e) {
      // ignore; still navigate
    }

    navigate('/login', { replace: true });

    // Fire and forget server-side logout (best effort).
    dispatch(logout()).catch((error) => {
      console.error('Logout error (ignored after navigation):', error);
    });
  };

  const getRoleName = (roleId) => {
    const roles = {
      1: 'Super Admin',
      2: 'Admin',
      3: 'Practice Admin',
      4: 'Provider',
      5: 'Practice Caregiver',
      6: 'Patient',
      7: 'System CareGiver'
    };
    return roles[roleId] || 'User';
  };

  // Build initials from first_name + last_name; fall back to username
  const getInitials = () => {
    const first = user?.first_name?.trim();
    const last = user?.last_name?.trim();
    if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
    if (first) return first.slice(0, 2).toUpperCase();
    if (last) return last.slice(0, 2).toUpperCase();
    if (user?.username) return user.username.slice(0, 2).toUpperCase();
    return '?';
  };

  useEffect(() => {
    if (activeScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeScreen]);

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleBreadcrumbClick = (path) => {
    if (path) navigate(path);
  };

  const handleHelpClick = () => {
    window.open('/assets/codexterlabs-User-Manual.pdf', '_blank', 'noopener');
  };

  const openScreen = (screen) => {
    setActiveScreen(screen);
    setShowUserDropdown(false);
  };

  const closeScreen = () => setActiveScreen(null);

  const rawBreadcrumbItems = Array.isArray(breadcrumbs) ? breadcrumbs : [];
  const breadcrumbItems = (() => {
    // Provider UX: show practice name as first crumb (clickable),
    // not generic "Practices" that navigates to the full practices list.
    if ([3, 4, 5, 7].includes(Number(user?.role_id)) && rawBreadcrumbItems.length >= 2) {
      const [first, second, ...rest] = rawBreadcrumbItems;
      if (first?.label === 'Practices' && second?.label) {
        return [
          {
            ...first,
            label: second.label,
            path: null,
            active: false
          },
          ...rest
        ];
      }
    }
    return rawBreadcrumbItems;
  })();
  const showBreadcrumbs = breadcrumbItems.length > 0;

  const handleNavigate = (path) => {
    navigate(path);
    setShowUserDropdown(false);
  };

  return (
    <header className="top-header" id="TopHeader">
      <div className={`header-left ${showBreadcrumbs ? '' : 'header-left--compact'}`}>
        <div className="logo">
          <img src={logo} alt="VitaRPM-logo" />
        </div>
        <button
          type="button"
          className="header-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={sidebarOpen}
          onClick={onToggleSidebar}
        >
          <img
            src={sidebarIcon}
            alt="Toggle Sidebar"
            className="header-toggle-icon"
          />
        </button>
        {showBreadcrumbs && (
          <div className="breadcrumb-wrapper">
            <nav aria-label="Breadcrumb" className="breadcrumb-nav">
              <ol className="breadcrumb">
                {breadcrumbItems.map((breadcrumb, index) => (
                  <li
                    key={index}
                    className={`breadcrumb-item ${breadcrumb.active ? 'active' : ''}`}
                    aria-current={breadcrumb.active ? 'page' : undefined}
                  >
                    {breadcrumb.active || !breadcrumb.path ? (
                      <span className="breadcrumb-current">
                        {breadcrumb.label}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="breadcrumb-link"
                        onClick={() => handleBreadcrumbClick(breadcrumb.path)}
                      >
                        {breadcrumb.label}
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        )}
      </div>

      <div className="header-right">
        <div
          className={`user-info${showUserDropdown ? ' user-info--open' : ''}`}
          ref={dropdownRef}
          onClick={() => setShowUserDropdown(!showUserDropdown)}
          role="button"
          aria-haspopup="true"
          aria-expanded={showUserDropdown}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setShowUserDropdown((v) => !v)}
        >
          {/* Initials Avatar */}
          <span className="user-avatar" aria-hidden="true">
            {getInitials()}
          </span>

          {/* Name + Role */}
          <span className="user-text">
            <span className="user-name">{user?.username}</span>
            <span className="user-role">{getRoleName(user?.role_id)}</span>
          </span>

          {/* Chevron */}
          <span className="user-chevron" aria-hidden="true">
            <FontAwesomeIcon icon={showUserDropdown ? faChevronUp : faChevronDown} />
          </span>

          {/* Dropdown */}
          {showUserDropdown && (
            <div className="user-dropdown" role="menu">

              {/* ── Identity Header ── */}
              <div className="dropdown-header">
                <div className="dropdown-header-avatar-wrap">
                  <span className="dropdown-header-avatar" aria-hidden="true">
                    {getInitials()}
                  </span>
                  <span className="dropdown-online-dot" title="Online" />
                </div>
                <div className="dropdown-header-info">
                  <span className="dropdown-header-name">{user?.username}</span>
                  <span className="dropdown-header-role">{getRoleName(user?.role_id)}</span>
                </div>
              </div>

              {/* ── Menu Items ── */}
              <div className="dropdown-body">

                <div className="dropdown-section-label">Account</div>

                <ul className="dropdown-links" role="none">
                  <li role="none">
                    <button
                      type="button"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => handleNavigate('/settings')}
                    >
                      <span className="dropdown-item-icon">
                        <FontAwesomeIcon icon={faGear} />
                      </span>
                      <span className="dropdown-item-content">
                        <span className="dropdown-item-label">Settings</span>
                        <span className="dropdown-item-desc">Manage your preferences</span>
                      </span>
                    </button>
                  </li>

                  <li role="none">
                    <button
                      type="button"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => openScreen('changePassword')}
                    >
                      <span className="dropdown-item-icon">
                        <FontAwesomeIcon icon={faShieldHalved} />
                      </span>
                      <span className="dropdown-item-content">
                        <span className="dropdown-item-label">Change Password</span>
                        <span className="dropdown-item-desc">Update your credentials</span>
                      </span>
                    </button>
                  </li>

                  {user?.role_id !== 6 && (
                    <li role="none">
                      <button
                        type="button"
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => openScreen('emailNotifications')}
                      >
                        <span className="dropdown-item-icon">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </span>
                        <span className="dropdown-item-content">
                          <span className="dropdown-item-label">Email Notifications</span>
                          <span className="dropdown-item-desc">Configure alerts & digests</span>
                        </span>
                      </button>
                    </li>
                  )}
                </ul>

                {/* ── Logout ── */}
                <div className="dropdown-divider" />
                <div className="dropdown-logout-wrap">
                  <button
                    type="button"
                    className="dropdown-item logout-item"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <span className="dropdown-item-icon">
                      <FontAwesomeIcon icon={faRightFromBracket} />
                    </span>
                    <span className="dropdown-item-content">
                      <span className="dropdown-item-label">Sign Out</span>
                      <span className="dropdown-item-desc">End your current session</span>
                    </span>
                  </button>
                </div>
              </div>

              {/* ── Footer ── */}
              <div className="dropdown-footer">

                {/* Support */}
                <div className="dropdown-footer-row dropdown-footer-links">
                  <a
                    href="mailto:support.vitarpm@codexterlabs.com"
                    className="dropdown-footer-action"
                  >
                    <FontAwesomeIcon icon={faCircleQuestion} />
                    Support
                  </a>
                </div>

                {/* Version + Environment */}
                <div className="dropdown-footer-row dropdown-footer-version">
                  <span className="dropdown-footer-badge">Version 1.0.8 - {process.env.NODE_ENV === 'production' ? 'Production' : 'Development'}</span>
                </div>

                {/* Credit */}
                <div className="dropdown-footer-row dropdown-footer-credit">
                  Developed &amp; Managed by{' '}
                  <a
                    href="https://codexterlabs.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dropdown-footer-link"
                  >
                    Codexter Labs
                  </a>
                </div>

              </div>

            </div>
          )}
        </div>
      </div>
      {activeScreen === 'changePassword' && (
        <ChangePasswordScreen onClose={closeScreen} />
      )}
      {activeScreen === 'emailNotifications' && (
        <EmailNotificationsScreen onClose={closeScreen} />
      )}
    </header>
  );
};

export default Header;

